import React, { useMemo } from 'react';

const DEFAULT_DATA = [
  { name: 'VM', total: 1 },
  { name: 'EJ', total: 0 },
  { name: 'WBG', total: 0 },
  { name: 'W2J', total: 0 },
];

const COLOR_PALETTE = {
  VM: '#96e6ab',
  EJ: '#76ace0',
  WBG: '#df87a8',
  W2J: '#f4d353',
};

const CHART_DIMENSIONS = {
  width: 750,
  height: 420,
  margin: { top: 36, right: 32, bottom: 96, left: 88 },
};

const clampNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numeric;
  }
  return fallback;
};

const buildTickValues = (maxValue) => {
  const safeMax = Math.max(1, Math.ceil(maxValue));
  if (safeMax <= 5) {
    return Array.from({ length: safeMax + 1 }, (_, index) => index);
  }

  const idealTickCount = 5;
  const roughStep = Math.max(1, Math.ceil(safeMax / idealTickCount));
  const ticks = [];
  for (let current = 0; current <= safeMax; current += roughStep) {
    ticks.push(current);
  }
  if (ticks[ticks.length - 1] !== safeMax) {
    ticks.push(safeMax);
  }
  return ticks;
};

function BluePigsChart({
  data = DEFAULT_DATA,
  title = 'FULL SCREEN OF BLUE PIGS',
  subtitle = 'Times Achieved',
  pigImageHref = '/images/pig.png',
  className = '',
}) {
  const { processedData, maxValue, ticks, chartWidth, chartHeight, bandStep, bandWidth } = useMemo(() => {
    const { width, height, margin } = CHART_DIMENSIONS;
    const chartWidthValue = width - margin.left - margin.right;
    const chartHeightValue = height - margin.top - margin.bottom;

    const processed = (data && Array.isArray(data) ? data : DEFAULT_DATA).map((entry, index) => {
      const name = entry?.name ?? entry?.label ?? `Player ${index + 1}`;
      const total = clampNumber(entry?.total ?? entry?.value ?? 0, 0);
      const fill = entry?.color ?? COLOR_PALETTE[name] ?? COLOR_PALETTE[Object.keys(COLOR_PALETTE)[index % 4]];
      return { name, total, fill };
    });

    const max = processed.reduce((acc, entry) => Math.max(acc, entry.total), 0);
    const tickValues = buildTickValues(max);
    const step = processed.length > 0 ? chartWidthValue / processed.length : chartWidthValue;
    const widthPerBand = step * 0.6;

    return {
      processedData: processed,
      maxValue: Math.max(max, 1),
      ticks: tickValues,
      chartWidth: chartWidthValue,
      chartHeight: chartHeightValue,
      bandStep: step,
      bandWidth: widthPerBand,
    };
  }, [data]);

  const { width, height, margin } = CHART_DIMENSIONS;
  const yScale = (value) => {
    const safeValue = clampNumber(value, 0);
    const ratio = maxValue === 0 ? 0 : safeValue / maxValue;
    return chartHeight - ratio * chartHeight;
  };

  const rootClassName = ['chart-container', 'chart-card', 'blue-pigs-chart', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClassName}>
      <h2>{title}</h2>
      {subtitle ? <p className="blue-pigs-chart__subtitle">{subtitle}</p> : null}
      <div className="blue-pigs-chart__svg-wrapper">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-labelledby="blue-pigs-title blue-pigs-desc"
          preserveAspectRatio="xMidYMid meet"
          className="blue-pigs-chart__svg"
        >
          <title id="blue-pigs-title">{title}</title>
          <desc id="blue-pigs-desc">
            Bar chart showing how many times each player has achieved a full screen of blue pigs.
          </desc>
          <g transform={`translate(${margin.left}, ${margin.top})`}>
            <line x1="0" x2={chartWidth} y1={chartHeight} y2={chartHeight} stroke="#bbb" strokeWidth="1" />
            {ticks.map((tick) => {
              const y = yScale(tick);
              return (
                <g key={tick} transform={`translate(0, ${y})`}>
                  <line x1="0" x2={chartWidth} y1="0" y2="0" stroke="#e5e7eb" strokeWidth={tick === 0 ? 2 : 1} />
                  <text
                    x={-12}
                    y={0}
                    textAnchor="end"
                    dominantBaseline="middle"
                    style={{ fill: '#4b5563', fontSize: '14px' }}
                  >
                    {tick}
                  </text>
                </g>
              );
            })}

            {processedData.map((entry, index) => {
              const x = index * bandStep + (bandStep - bandWidth) / 2;
              const barTop = yScale(entry.total);
              const barHeight = chartHeight - barTop;
              const imageSize = 100;
              const imageX = x + bandWidth / 2 - imageSize / 2;
              const imageY = barTop + barHeight / 2 - imageSize / 2;

              return (
                <g key={entry.name}>
                  <rect
                    x={x}
                    y={barTop}
                    width={bandWidth}
                    height={barHeight}
                    fill={entry.fill}
                    rx={8}
                    aria-label={`${entry.name} has achieved ${entry.total} time${entry.total === 1 ? '' : 's'}`}
                  >
                    <title>{`${entry.name}: ${entry.total} time${entry.total === 1 ? '' : 's'}`}</title>
                  </rect>
                  {entry.total > 0 && pigImageHref ? (
                    <image
                      href={pigImageHref}
                      x={imageX}
                      y={Math.max(margin.top * -0.25, imageY)}
                      width={imageSize}
                      height={imageSize}
                      preserveAspectRatio="xMidYMid meet"
                      style={{ pointerEvents: 'none' }}
                    />
                  ) : null}
                  <text
                    x={x + bandWidth / 2}
                    y={chartHeight + 32}
                    textAnchor="middle"
                    style={{ fill: '#1f2937', fontSize: '16px', fontWeight: 600 }}
                  >
                    {entry.name}
                  </text>
                  <text
                    x={x + bandWidth / 2}
                    y={chartHeight + 52}
                    textAnchor="middle"
                    style={{ fill: '#4b5563', fontSize: '14px' }}
                  >
                    {`${entry.total} ${entry.total === 1 ? 'time' : 'times'}`}
                  </text>
                </g>
              );
            })}
          </g>
        </svg>
      </div>
    </div>
  );
}

export default BluePigsChart;
